import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/lib/utils';
import { Label } from './label';
import { Switch } from './switch';

const switchFieldVariants = cva('w-full', {
  variants: {
    variant: {
      default: '',
      box: 'rounded-[10px] border border-base-border bg-base-background p-4 shadow-xs',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type SwitchFieldProps = Omit<React.ComponentPropsWithoutRef<typeof Switch>, 'id'> &
  VariantProps<typeof switchFieldVariants> & {
    id?: string;
    label: React.ReactNode;
    description?: React.ReactNode;
    /** Switch before label (Figma Side=Left) or after (Side=Right). */
    switchPosition?: 'left' | 'right';
    wrapperClassName?: string;
  };

const SwitchField = React.forwardRef<React.ElementRef<typeof Switch>, SwitchFieldProps>(
  (
    {
      id: idProp,
      label,
      description,
      switchPosition = 'left',
      variant,
      wrapperClassName,
      className,
      disabled,
      ...switchProps
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const id = idProp ?? generatedId;
    const labelId = `${id}-label`;
    const descriptionId = description ? `${id}-description` : undefined;

    return (
      <div className={cn(switchFieldVariants({ variant }), wrapperClassName)}>
        <div className="flex items-start gap-3">
          {switchPosition === 'left' ? (
            <Switch
              ref={ref}
              id={id}
              disabled={disabled}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
              className={cn('mt-0.5 shrink-0', className)}
              {...switchProps}
            />
          ) : null}
          <div
            className={cn(
              'flex min-w-0 flex-1 flex-col gap-2',
              variant === 'default' && switchPosition === 'left' && 'pt-0.5',
            )}
          >
            <Label
              id={labelId}
              htmlFor={id}
              className={cn(
                'cursor-pointer text-sm font-medium leading-none text-base-foreground',
                disabled && 'cursor-not-allowed opacity-70',
              )}
            >
              {label}
            </Label>
            {description ? (
              <p id={descriptionId} className="text-sm text-base-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          {switchPosition === 'right' ? (
            <Switch
              ref={ref}
              id={id}
              disabled={disabled}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
              className={cn('mt-0.5 shrink-0', className)}
              {...switchProps}
            />
          ) : null}
        </div>
      </div>
    );
  },
);
SwitchField.displayName = 'SwitchField';

export { SwitchField };
export type { SwitchFieldProps };
